import React from "react";
import styled, { css } from "styled-components";
import _ from "lodash";
import { toast } from "react-toastify";

import { Button } from "src/library/components/Button";
import {
  BasicModal,
  BasicModalProps
} from "src/library/components/Modal/BasicModal";
import { Text } from "src/library/components/Text";
import { ITerminology } from "src/library/utils/string-manipulations/tokenize-string";
import { Select } from "src/library/components/Forms";
import { ICustomer } from "src/core/customers/api/customers.api";
import {
  IDrop,
  useDropsApi
} from "src/core/drops/api/drops.api";
import { LoadingSpinner } from "src/library/components/LoadingSpinner";
import SelectedCustomer from "src/core/customers/components/SelectedCustomer";
import { ErrorNotice } from "src/core/shared/components/ErrorNotice";

import { IFormOption } from "../../../library/components/Forms";

export interface MoveUserModelOnSave {
  customerId: number | null;
  defaultDrop: IDrop | null;
}

export interface MoveUserModalProps extends Pick<BasicModalProps, "onDismiss" | "open"> {
  onSave: (data: MoveUserModelOnSave) => void;
  selectedCustomer?: ICustomer;
  selectedDefaultDrop?: IDrop;
  terminology?: ITerminology;
  isLoading?: boolean;
}

export const MoveUserModal: React.ReactFCWithChildren<MoveUserModalProps> = ({
  onDismiss, open, onSave, selectedCustomer, selectedDefaultDrop, terminology, isLoading
}) => {
  const [ dropOptions, setDropOptions ] = React.useState<IFormOption[]>([]);
  const [ dropsResponse, getDrops ] = useDropsApi("LIST_DROPS_BY_CUSTOMER");
  const [ customer, setCustomer ] = React.useState<ICustomer | undefined>();
  const [ defaultDrop, setDefaultDrop ] = React.useState<IDrop | undefined>();
  const dropTermSingular = terminology?.drop.singular.text || "drop";
  const dropTermPlural = terminology?.drop.plural.text || "drops";

  const drops = React.useMemo(() => {
    if (dropsResponse.data) {
      return dropsResponse.data.result;
    }

    return [];
  }, [ dropsResponse ]);

  // keep the modal up-to-date with parent
  React.useEffect(() => {
    setCustomer(selectedCustomer);
    setDefaultDrop(selectedDefaultDrop);
  }, [ selectedCustomer, selectedDefaultDrop ]);

  // request drop options whenever customer changes
  React.useEffect(() => {
    if (customer?.id) {
      getDrops({
        customerId: customer.id,
        pagination: {},
        filters: {}
      });
    }
  }, [ customer?.id, getDrops ]);

  // handle drops response
  React.useEffect(() => {
    if (dropsResponse.data) {
      setDropOptions(dropsResponse.data.result.map(drop => ({
        text: drop.branchNumber,
        value: drop.id.toString()
      })));
    }

    if (dropsResponse.error) {
      toast.error(`An unexpected error occurred - ${dropsResponse.error.message}`);
    }
  }, [ dropsResponse ]);

  return (
    <StyledBasicModal
      title="Move User"
      open={open}
      onDismiss={() => onDismiss()}
    >
      <ErrorNotice
        type="warn"
        message={`When just updating default ${dropTermSingular}, user will maintain access to any ${dropTermPlural} they previously had.`}
      />

      <Text>
        {`Here you are able to move a user from their current customer to another customer. 
        By doing this, you will need to reset their default ${dropTermSingular}.`}
      </Text>

      <div className="field">
        <Text weight="bold">
          {_.capitalize(terminology?.customer.singular.text) || "Customer"}
        </Text>

        <SelectedCustomer
          customer={customer || null}
          updateCustomer={newCustomer => {
            setCustomer(newCustomer);
            // reset default drop and user now needs to select a new default drop as customer has changed
            setDefaultDrop(undefined);
          }}
          terminology={terminology}
          disableRemoveCustomer
        />
      </div>

      <div className="field">
        <Text weight="bold">
          {`Default ${dropTermSingular}`}
        </Text>

        {dropsResponse.pending ?
          (
            <LoadingSpinner
              type="flat"
              position="inline"
            />
          ) :
          !drops.length ?
            (
              <div>
                <Text className="no-drop">
                  {`No ${_.capitalize(terminology?.drop.plural.text) || "Drops"} associated with this customer. Please select another customer to continue.`}
                </Text>
              </div>
            ) :
            (
              <Select
                options={dropOptions}
                defaultValue={defaultDrop?.id.toString() || ""}
                value={defaultDrop?.id.toString() || ""}
                as="select"
                name="Drop"
                autoHideLabels
                label="drop"
                handleChange={e => {
                  const newlySelectedDrop = _.find(drops, drop => drop.id === parseInt(e.target.value));

                  if (newlySelectedDrop) {
                    setDefaultDrop(newlySelectedDrop);
                  }
                }}
              />
            )}
      </div>

      <Button
        className="action-save-button"
        disabled={!drops.length || !defaultDrop}
        loading={isLoading}
        onClick={() => onSave({
          customerId: customer?.id !== undefined ? customer.id : null,
          defaultDrop: defaultDrop !== undefined ? defaultDrop : null
        })}
      >
        Save
      </Button>
    </StyledBasicModal>
  );
};

const StyledBasicModal = styled(BasicModal)`
  ${({ theme }) => css`    
    max-height: calc(100vh - 40px);
    overflow-y: scroll;
    min-height: 400px;

    .field {
      margin: ${theme.spacing.margins.md} 0;
    }

    .field > :nth-child(2) {
      margin-top: ${theme.spacing.margins.sm};
      max-width: 100%;
    }

    .field > :nth-child(2) > :first-child {
      max-width: 100%;
    }

    .no-drop {
      color: ${theme.colors.error};
    }

    .action-save-button {
      margin-top: ${theme.spacing.margins.md};
    }

    .loading-container {
      width: 100%;
    }
  `}
`;
